import {
  APIFieldType,
  APISurveyAnswer,
  APISurveyDependentQuestionAnswer,
  APISurveyReferenceAnswer,
  APISurveyReferenceAnswerValue,
  ArdoqId,
  PersistedConditionFieldQuestion,
  PersistedSurveyQuestion,
} from '@ardoq/api-types';
import {
  isAttributeQuestion,
  isHiddenQuestion,
} from '../questions/questionPredicates';
import {
  Answers,
  AttributeAnswer,
  AttributeAnswerValue,
  ConditionAnswer,
  DateRange,
  FieldAnswer,
  FieldAnswerValue,
  ParentAnswer,
  ReferenceAnswer,
  ReferenceAnswerValue,
  TagAnswer,
} from '../types';
import {
  isAPISurveyDependentQuestionAnswer,
  isAPISurveyParentAnswer,
  isAPISurveyReferenceAnswer,
  isAttributeAnswer,
  isConditionAnswer,
} from './answerPredicates';
import { Locale, localeCompareNumeric } from '@ardoq/locale';
import { toId } from '@ardoq/core';

export const getSpecificAttributeAnswer = (
  questions: PersistedSurveyQuestion[],
  attributeName: string,
  answers: Answers | undefined
): AttributeAnswerValue => {
  if (!answers) return null;
  const attributeQuestion = questions
    .filter(isAttributeQuestion)
    .find(({ properties }) => properties.attributeName === attributeName);
  if (!attributeQuestion) return null;
  const attributeAnswer = answers[attributeQuestion._id];
  return isAttributeAnswer(attributeAnswer) ? attributeAnswer?.value : null;
};

export const getNameAnswer = (
  questions: PersistedSurveyQuestion[],
  answers: Answers
): string => getSpecificAttributeAnswer(questions, 'name', answers) ?? '';

export const getParsedConditionAnswer = (
  conditionAnswer: any,
  conditionFieldType?: APIFieldType
) => {
  switch (conditionFieldType) {
    case APIFieldType.CHECKBOX:
      return conditionAnswer === null ? undefined : conditionAnswer;
    case APIFieldType.LIST:
      return conditionAnswer;
    default:
      return conditionAnswer;
  }
};

export const getConditionAnswer = (
  question: PersistedConditionFieldQuestion,
  answers: Answers | undefined
): ConditionAnswer | undefined => {
  const answer = answers?.[question._id];
  if (isConditionAnswer(answer)) {
    return answer;
  }
};

export const getReferenceQuestionAnswer = (
  questionId: ArdoqId,
  answers: Answers | undefined
): ReferenceAnswer | undefined => {
  return answers
    ? (answers[questionId] as ReferenceAnswer | undefined)
    : undefined;
};

export const getReferenceId = (ref: ReferenceAnswerValue): string =>
  ref.referenceId ?? ref.tempId;

export const getTagQuestionAnswer = (
  questionId: ArdoqId,
  answers: Answers | undefined
): TagAnswer | undefined => {
  return answers?.[questionId] as TagAnswer | undefined;
};

export const getAttributeQuestionAnswer = (
  questionId: ArdoqId,
  answers: Answers | undefined
): AttributeAnswer | ParentAnswer | undefined => {
  return answers?.[questionId] as AttributeAnswer | ParentAnswer | undefined;
};

export const getFieldQuestionAnswer = (
  questionId: ArdoqId,
  answers: Answers | undefined
): FieldAnswer<FieldAnswerValue> | undefined => {
  return answers?.[questionId] as FieldAnswer<FieldAnswerValue> | undefined;
};

export const getConditionalFieldQuestionAnswers = (
  answer: ConditionAnswer | undefined
): Record<ArdoqId, FieldAnswer<FieldAnswerValue> | ReferenceAnswer> => {
  return (
    answer?.answers ??
    ({} as Record<ArdoqId, FieldAnswer<FieldAnswerValue> | ReferenceAnswer>)
  );
};

export const getDateRangeFieldQuestionAnswer = (
  questionId: ArdoqId,
  answers: Answers
): FieldAnswer<DateRange> | undefined => {
  return answers[questionId] as FieldAnswer<DateRange> | undefined;
};
const getReferenceAnswers = (
  item: APISurveyReferenceAnswer,
  currentLocale: Locale
) => {
  return item.value
    .sort(
      (a: APISurveyReferenceAnswerValue, b: APISurveyReferenceAnswerValue) =>
        localeCompareNumeric(a.component.name, b.component.name, currentLocale)
    )
    .map((ref: APISurveyReferenceAnswerValue) => ({
      ...ref,
      component: {
        ...ref.component,
        name: ref.component.name,
      },
      answers: ref.answers.reduce(
        (nestedAcc, nestedAnswer) => ({
          ...nestedAcc,
          [nestedAnswer.questionId]: {
            originalValue: nestedAnswer.value,
            value: nestedAnswer.value,
            error: '',
          },
        }),
        {}
      ),
    }));
};

const formatReferenceAnswer = (
  item: APISurveyReferenceAnswer,
  originalValue: any
) => {
  return {
    [item.questionId]: {
      totalReferences: item.totalReferences,
      originalValue: originalValue,
      value: originalValue,
      error: '',
    },
  };
};

const getNestedAnswer = (item: APISurveyDependentQuestionAnswer) => {
  return {
    originalValue: item.value,
    value: item.value,
    error: '',
    answers: item.answers.reduce((nestedAcc, nestedAnswer) => {
      if (isAPISurveyReferenceAnswer(nestedAnswer)) {
        return {
          ...nestedAcc,
          ...formatReferenceAnswer(nestedAnswer, nestedAnswer.value),
        };
      }
      return {
        ...nestedAcc,
        [nestedAnswer.questionId]: {
          originalValue: nestedAnswer.value,
          value: nestedAnswer.value,
          error: '',
        },
      };
    }, {}),
  };
};

export const getAnswersFromAPISurveyAnswers = (
  answerList: APISurveyAnswer[],
  currentLocale: Locale
): Answers =>
  answerList.reduce((acc: Answers, item: APISurveyAnswer): Answers => {
    if (isAPISurveyReferenceAnswer(item)) {
      const updatedAnswer = getReferenceAnswers(item, currentLocale);
      return {
        ...acc,
        ...formatReferenceAnswer(item, updatedAnswer),
      };
    }
    if (isAPISurveyDependentQuestionAnswer(item)) {
      const updatedAnswer = getNestedAnswer(item);
      return {
        ...acc,
        [item.questionId]: updatedAnswer,
      };
    }
    if (isAPISurveyParentAnswer(item)) {
      const updatedAnswer = {
        originalValue: item.value,
        value: item.value,
        error: '',
        parentName: item.parentName,
      };
      return {
        ...acc,
        [item.questionId]: updatedAnswer,
      };
    }
    const updatedAnswer = {
      originalValue: item.value,
      value: item.value,
      error: '',
    };
    return {
      ...acc,
      [item.questionId]: updatedAnswer,
    };
  }, {});

export const getHiddenQuestionAnswers = (
  questions: PersistedSurveyQuestion[],
  answers: Answers
): Answers => {
  const hiddenQuestionIds = questions.filter(isHiddenQuestion).map(toId);

  return Object.fromEntries(
    Object.entries(answers).filter(([questionId]) =>
      hiddenQuestionIds.includes(questionId)
    )
  );
};
